import { useState } from 'react';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { Menu, Music, Mic, Volume2, VolumeX, Play, Pause, Headphones, MoreHorizontal, Download, Drum, Loader2, Wand2, AudioLines, SlidersHorizontal, Scissors, Rows, UserPlus, Radio, PlusCircle, AlertCircle } from 'lucide-react';
import AudioUploader from '@/components/AudioUploader';
import { SessionHeader } from '@/components/session/SessionHeader';
import { ProcessingStatus } from '@/components/session/ProcessingStatus';
import { StemsList } from '@/components/session/StemsList';
import WaveformDisplay from '@/components/WaveformDisplay';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import { useMultitrack } from '@/components/multitrack/useMultitrack';
import { parseSongInfo, sanitizeFilename, getFileExtension } from '@/utils/songUtils';
import { withPosthogId } from '@/utils/analytics';
import type { Stem } from '@/types/stem';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DonationModal } from '@/components/DonationModal';
import { DonationBanner } from '@/components/DonationBanner';
// import { SplitLimitDisplay } from '@/components/SplitLimitDisplay';
import seshPromo from "@/assets/sesh-promo.png";
import { isSessionExpired, EXPIRED_MESSAGE } from '@/utils/sessionExpiry';
interface SessionContentProps {
  session: any;
  isProcessing: boolean;
  progress: number;
  processingError: string | null;
  processingDuration: number;
  onRetry: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  onFileSelect: (file: File, type: 'stem' | 'acapella' | 'drum') => void;
  sessionType: 'stem' | 'acapella' | 'drum';
  showDonationModal?: boolean;
  donationModalSessionId?: string | null;
  onDonationModalClose?: () => void;
  showDonationBanner?: boolean;
  onBannerClose?: () => void;
}
export const SessionContent = ({
  session,
  isProcessing,
  progress,
  processingError,
  processingDuration,
  onRetry,
  onDelete,
  onFileSelect,
  sessionType,
  showDonationModal,
  donationModalSessionId,
  onDonationModalClose,
  showDonationBanner,
  onBannerClose
}: SessionContentProps) => {
  const isMobile = useIsMobile();
  const {
    toggleSidebar
  } = useSidebar();
  const [showMultitrack, setShowMultitrack] = useState(true);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const handleDownload = async (stem: Stem) => {
    try {
      setIsDownloading(true);
      toast.info(`Downloading ${stem.name}...`);
      const response = await fetch(stem.url);
      const blob = await response.blob();
      const sanitizedOriginalName = sanitizeFilename(session?.original_filename || 'audio');
      const sanitizedStemName = stem.name.toLowerCase().replace(/\s+/g, '_');
      const extension = stem.name === 'Original' ? getFileExtension(session?.original_filename || 'audio.m4a') : '.m4a';
      const filename = `${sanitizedOriginalName}_${sanitizedStemName}${extension}`;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Downloaded ${stem.name} successfully`);
    } catch (error) {
      console.error('Error downloading stem:', error);
      toast.error(`Failed to download ${stem.name}`);
    } finally {
      setIsDownloading(false);
    }
  };
  const handleDownloadAll = async () => {
    if (!session?.stems) return;
    try {
      setIsDownloading(true);
      toast.info('Your stems are downloading... they may take a while');

      // PostHog tracking disabled
      // const songInfo = parseSongInfo(session?.original_filename || '');
      // posthog.capture('Download stems CTA Clicked', {
      //   sessionId: session?.id,
      //   sessionType,
      //   songTitle: songInfo.title,
      //   artist: songInfo.artist,
      //   source: 'actions-grid',
      //   stemsCount: (session?.stems as Stem[])?.length || 0,
      //   originalExtension: getFileExtension(session?.original_filename || 'audio.m4a')
      // });

      const sanitizedOriginalName = sanitizeFilename(session?.original_filename || 'audio');
      const downloadPromises = (session.stems as Stem[]).map(async stem => {
        try {
          const response = await fetch(stem.url);
          const blob = await response.blob();
          const sanitizedStemName = stem.name.toLowerCase().replace(/\s+/g, '_');
          const extension = stem.name === 'Original' ? getFileExtension(session?.original_filename || 'audio.m4a') : '.m4a';
          const filename = `${sanitizedOriginalName}_${sanitizedStemName}${extension}`;
          return {
            name: filename,
            blob
          };
        } catch (error) {
          console.error(`Error downloading stem ${stem.name}:`, error);
          return null;
        }
      });
      const downloads = await Promise.all(downloadPromises);
      const validDownloads = downloads.filter(download => download !== null);
      if (validDownloads.length === 0) {
        throw new Error('No stems could be downloaded');
      }
      const zip = new JSZip();
      validDownloads.forEach(({
        name,
        blob
      }) => {
        zip.file(name, blob);
      });
      const zipBlob = await zip.generateAsync({
        type: 'blob'
      });
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${sanitizedOriginalName}_stems.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Downloaded ${validDownloads.length} stems successfully`);
    } catch (error) {
      console.error('Error downloading all stems:', error);
      toast.error('Failed to download stems');
    } finally {
      setIsDownloading(false);
    }
  };
  const {
    containerRef,
    isPlaying,
    tracks,
    masterVolume,
    togglePlayPause,
    setTrackVolume,
    setTrackMute,
    setTrackSolo,
    setGlobalVolume,
    loadProgress
  } = useMultitrack(session?.stems as Stem[] || [], session?.original_audio_url || '');
  const extractHashFromUrl = (url: string): string | null => {
    const prefix = 'https://daw.api.sesh.fm/audio/sample/';
    if (url.startsWith(prefix)) {
      const hash = url.substring(prefix.length);

      // Validate SHA256 hash format
      if (/^[a-fA-F0-9]{64}$/.test(hash)) {
        return hash;
      }
    }
    return null;
  };
  const handleDawClick = async () => {
    // PostHog tracking disabled
    // const songInfo = parseSongInfo(session?.original_filename || '');
    // posthog.capture('DAW CTA Clicked', {
    //   songTitle: songInfo.title,
    //   artist: songInfo.artist
    // });

    try {
      // Find the original stem (first one is usually original)
      const originalStem = (session?.stems as Stem[])?.[0];
      if (!originalStem) {
        throw new Error('No stems available');
      }

      // Extract hash from original audio URL
      const originalHash = extractHashFromUrl(session?.original_audio_url || '');
      if (!originalHash) {
        throw new Error('Cannot extract audio hash');
      }

      // Check for required fields early
      if (!session?.original_filename) {
        throw new Error('No original filename available');
      }

      // Find the original track state
      const originalTrack = tracks.find(track => track.name.toLowerCase() === 'original');

      // Build the stems array (excluding the original)
      const stemsData: Array<[string, any]> = [];
      const stemsList = (session?.stems as Stem[]).slice(1); // Skip original

      stemsList.forEach((stem, index) => {
        const hash = extractHashFromUrl(stem.url);
        if (hash) {
          const track = tracks.find(t => t.name === stem.name);
          stemsData.push([stem.name, {
            sample_sha256: hash,
            duration_secs: stem.duration,
            muted: track?.isMuted || false,
            soloed: track?.isSoloed || false
          }]);
        }
      });

      // Build the API payload
      const payload = {
        original_file_name: session.original_filename,
        original: {
          sample_sha256: originalHash,
          duration_secs: originalStem.duration,
          muted: originalTrack?.isMuted || false,
          soloed: originalTrack?.isSoloed || false
        },
        stems: stemsData
      };

      // Make the API call
      const response = await fetch('https://daw.api.sesh.fm/app/import-links', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }
      const uuid = await response.json();

      // Redirect to Sesh with the import UUID
      if (uuid && typeof uuid === 'string') {
        window.open(withPosthogId(`https://sesh.fm/import/${uuid}`), '_blank');
      } else {
        throw new Error('Invalid response from import API');
      }
    } catch (error) {
      console.error('Error calling Sesh import API:', error);
      toast.error('Failed to import to Sesh');
      // Fallback to the original redirect
      window.open(withPosthogId('https://sesh.fm/get-started'), '_blank');
    }
  };
  const captureCtaAndImport = async (eventName: string) => {
    try {
      setIsImporting(true);
      // PostHog tracking disabled
      // const songInfo = parseSongInfo(session?.original_filename || '');
      // posthog.capture(eventName, {
      //   songTitle: songInfo.title,
      //   artist: songInfo.artist,
      //   sessionId: session?.id,
      //   sessionType,
      //   source: 'actions-grid'
      // });
      await handleDawClick();
    } finally {
      setIsImporting(false);
    }
  };
  if (!session) {
    return <div className="p-4 md:p-8 space-y-4">
        <DonationBanner isVisible={showDonationBanner || false} onClose={onBannerClose || (() => {})} sessionId={null} sessionType={sessionType} />
        {/* <SplitLimitDisplay sessionType={sessionType} /> */}
        
        <div className="flex items-center gap-2 mb-4">
          {isMobile && <Button variant="ghost" size="icon" className="h-10 w-10" onClick={toggleSidebar}>
              <Menu className="h-4 w-4" />
            </Button>}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {sessionType === 'stem' ? <Music className="h-6 w-6 text-white" /> : sessionType === 'acapella' ? <Mic className="h-6 w-6 text-white" /> : <Drum className="h-6 w-6 text-white" />}
              <h1 className="text-2xl font-semibold tracking-tight text-white truncate max-w-[22ch]">
                {sessionType === 'stem' ? 'AI Stem Splitter' : sessionType === 'acapella' ? 'Acapella Extractor' : 'Drum Remover'}
              </h1>
            </div>
            <p className="text-sm text-gray-400">
              {sessionType === 'stem' ? 'Upload audio to split stems' : sessionType === 'acapella' ? 'Upload audio to extract acapellas' : 'Upload music files and extract the drum tracks in one click'}
            </p>
          </div>
        </div>
        <AudioUploader onFileSelect={file => onFileSelect(file, sessionType)} />
        {isProcessing && <ProcessingStatus isProcessing={true} progress={progress} error={null} processingDuration={processingDuration} />}
        {processingError && <div role="alert" className="mx-auto flex max-w-xl items-start gap-3 rounded-lg border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-amber-100">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="text-sm font-medium">Choose a shorter audio file</p>
              <p className="mt-0.5 text-sm text-amber-100/70">{processingError}</p>
            </div>
          </div>}
      </div>;
  }
  const expired = isSessionExpired(session);

  return <div className="space-y-4 md:space-y-6 p-4 md:p-8 w-full overflow-x-hidden max-h-screen">
      <DonationBanner isVisible={showDonationBanner || false} onClose={onBannerClose || (() => {})} sessionId={session?.id || null} sessionType={sessionType} />
      {/* <SplitLimitDisplay sessionType={sessionType} /> */}
      
      <SessionHeader filename={session?.original_filename} sessionId={session?.id} onDelete={() => session?.id && onDelete(session.id)} onDownloadAll={handleDownloadAll} showDownload={!expired && session?.status === 'completed' && session?.stems && (session.stems as Stem[]).length > 0} isDownloading={isDownloading} />

      {expired && <div className="flex items-start gap-3 rounded-lg border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-amber-100/90 max-w-xl">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-sm">{EXPIRED_MESSAGE}</p>
        </div>}

      {!expired && session.status === 'processing' && isProcessing && <div className="space-y-4 md:space-y-6 max-w-full">
          <div className="bg-card rounded-lg max-w-full">
            <WaveformDisplay audioUrl={session.original_audio_url} name="Original" index={0} />
          </div>
          <ProcessingStatus isProcessing={true} progress={progress} error={null} onRetry={() => {}} processingDuration={processingDuration} />
        </div>}

      {!expired && session.status === 'failed' && !processingError && <ProcessingStatus isProcessing={isProcessing} progress={progress} error="There was an error processing your audio file." onRetry={() => onRetry(session.id)} processingDuration={processingDuration} />}

      {!expired && processingError && <ProcessingStatus isProcessing={isProcessing} progress={progress} error={processingError} onRetry={() => onRetry(session.id)} processingDuration={processingDuration} />}

      {!expired && session.status === 'processing' && !isProcessing && !processingError && <ProcessingStatus isProcessing={false} progress={progress} error="This processing session did not complete. Please try again." onRetry={() => onRetry(session.id)} processingDuration={processingDuration} />}

      {!expired && session?.stems && session.status === 'completed' && (session.stems as Stem[]).length > 0 && <div className="space-y-4 w-full">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between w-full">
              <div className="flex items-center gap-6">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant={isPlaying ? "default" : "outline"} size="icon" onClick={togglePlayPause} className={`h-12 w-12 rounded-full ${isPlaying ? "bg-[#3B82F6] hover:bg-[#60A5FA] text-white" : "bg-white hover:bg-white/90"}`}>
                        {isPlaying ? <Pause className="h-6 w-6 text-white" /> : <Play className="h-6 w-6 text-black" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isPlaying ? "Pause stems" : "Play stems"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-muted-foreground">Master</span>
                  <div className="flex items-center gap-2 w-32 md:w-48">
                    <VolumeX className="h-4 w-4" />
                    <Slider value={[masterVolume * 100]} onValueChange={value => setGlobalVolume(value[0] / 100)} max={100} step={1} className="[&_.relative.h-1]:h-[3px]" />
                    <Volume2 className="h-4 w-4" />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap justify-end">

                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      
                    </TooltipTrigger>
                    <TooltipContent>
                      Edit & remix stems in Sesh
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="default" className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white h-10 px-4 flex-1 md:flex-none" onClick={handleDownloadAll} disabled={isDownloading} aria-label="Download all stems">
                        <Download className="w-4 h-4 mr-2" />
                        <span className="hidden md:inline">Download stems</span>
                        <span className="md:hidden">Download</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Download all stems as a ZIP file
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div className="w-full overflow-x-scroll" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
              <div className="flex max-w-full relative" style={{
            overflow: 'visible'
          }}>
                <div className="w-fit flex-shrink-0 flex flex-col gap-[1px] rounded-lg overflow-hidden z-30">
                  {tracks.map(track => <div key={track.id} className="flex items-center gap-2 px-2 bg-card w-fit flex-shrink-0" style={{
                height: '49px',
                lineHeight: '1'
              }}>
                      <span className="w-24 text-sm truncate leading-none">{track.name}</span>
                      
                      <div className="flex items-center gap-2 w-20 h-full">
                        <Slider value={[track.volume * 100]} onValueChange={value => setTrackVolume(track.id, value[0] / 100)} max={100} step={1} className="[&_.relative.h-1]:h-[3px]" />
                      </div>

                      <div className="flex items-center gap-1 h-full">
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant={track.isMuted ? "destructive" : "ghost"} size="icon" onClick={() => setTrackMute(track.id)} className="h-8 w-8 flex items-center justify-center">
                                <VolumeX className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Mute track
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant={track.isSoloed ? "default" : "ghost"} size="icon" onClick={() => setTrackSolo(track.id)} className={`h-8 w-8 flex items-center justify-center ${track.isSoloed ? "bg-[#3B82F6] hover:bg-[#60A5FA] text-white" : ""}`}>
                                <Headphones className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Solo track
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>)}
                </div>

                <div className="relative flex-1 w-0">
                  <div className="absolute inset-0 rounded-lg opacity-25 z-0" style={{
                background: `linear-gradient(180deg, ${tracks.map(track => track.color).join(', ')})`,
                height: `${tracks.length * 49 + (tracks.length - 1)}px`
              }} />
                  
                  <div id="loading-bars-container" data-testid="loading-bars-container" className="absolute inset-0 z-10 pointer-events-none flex flex-col gap-[1px]" style={{
                height: `${tracks.length * 49 + (tracks.length - 1)}px`
              }}>
                    {tracks.map(track => <div key={`loading-${track.id}`} id={`loading-track-${track.id}`} data-testid={`loading-track-${track.id}`} style={{
                  height: '49px'
                }} className="relative">
                        <div id={`loading-background-${track.id}`} data-testid={`loading-background-${track.id}`} className="absolute inset-0 bg-black/20">
                          <div id={`loading-bar-${track.id}`} data-testid={`loading-bar-${track.id}`} className={`absolute inset-0 transition-all duration-300 ease-out ${loadProgress[track.id] === 100 ? 'opacity-0' : ''}`} style={{
                      width: `${loadProgress[track.id] || 0}%`,
                      backgroundColor: track.color,
                      opacity: 0.15,
                      transitionProperty: 'width, opacity',
                      transitionDuration: '300ms'
                    }}>
                            <div id={`loading-pulse-${track.id}`} data-testid={`loading-pulse-${track.id}`} className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                          </div>
                        </div>
                      </div>)}
                  </div>

                  <div ref={containerRef} className="relative rounded-lg overflow-hidden z-20" style={{
                height: `${tracks.length * 49 + (tracks.length - 1)}px`
              }} />
                </div>
              </div>
            </div>
            
            {/* Sesh Promo Banner */}
            <a href="https://sesh.fm" target="_blank" rel="noopener noreferrer" className="block w-full hover-scale rounded-lg overflow-hidden transition-transform duration-200 mb-4">
              <img src={seshPromo} alt="Sesh - Multiplayer DAW. Try it for free today" className="w-full h-auto" />
            </a>
            
            
          </div>
        </div>}
      
      <DonationModal isOpen={showDonationModal || false} onClose={onDonationModalClose || (() => {})} sessionId={donationModalSessionId} sessionType={sessionType} />
    </div>;
};